import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, isAuthenticated, token } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const pollingRef = useRef(false);
  
  const sessionId = searchParams.get("session_id");
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    // Prevent double polling
    if (pollingRef.current) return;
    pollingRef.current = true;

    const pollPaymentStatus = async (attemptCount = 0) => {
      if (!sessionId) {
        setError("No session ID found. Please try subscribing again.");
        setVerifying(false);
        return;
      }

      // Check authentication - if no token after a few attempts, redirect to login
      if (!token) {
        if (attemptCount < 3) {
          // Wait for auth to be ready
          setTimeout(() => pollPaymentStatus(attemptCount + 1), 1000);
          return;
        } else {
          // Not authenticated, redirect to auth with a message
          setError("Please log in to verify your subscription.");
          setVerifying(false);
          return;
        }
      }

      if (attemptCount >= maxAttempts) {
        // On timeout, check if user is already premium (maybe webhook processed it)
        try {
          const userResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (userResponse.data?.is_premium) {
            setVerified(true);
            setVerifying(false);
            await refreshUser();
            toast.success("Subscription confirmed! Welcome to Premium!");
            setTimeout(() => {
              navigate("/quizzes", { replace: true });
            }, 1500);
            return;
          }
        } catch (e) {
          console.error("Failed to check user status:", e);
        }
        setError("Payment verification timed out. If you completed payment, please refresh the page or contact support.");
        setVerifying(false);
        return;
      }

      try {
        setAttempts(attemptCount + 1);
        
        const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log("Payment status response:", response.data);
        
        if (response.data.payment_status === "paid" || response.data.is_premium) {
          setVerified(true);
          setVerifying(false);
          
          // Refresh user data to get updated premium status
          await refreshUser();
          
          toast.success("Subscription activated! Welcome to Premium!");
          
          // Redirect to quizzes immediately
          setTimeout(() => {
            navigate("/quizzes", { replace: true });
          }, 1500);
          
        } else if (response.data.status === "expired") {
          setError("Payment session expired. Please try again.");
          setVerifying(false);
        } else {
          // Payment still pending, continue polling
          setTimeout(() => pollPaymentStatus(attemptCount + 1), pollInterval);
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        
        // If 404 (transaction not found), might be an invalid session
        if (error.response?.status === 404) {
          setError("Invalid payment session. Please try subscribing again.");
          setVerifying(false);
          return;
        }
        
        if (attemptCount < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attemptCount + 1), pollInterval);
        } else {
          setError("Failed to verify payment. Please contact support if payment was completed.");
          setVerifying(false);
        }
      }
    };

    // Start polling
    pollPaymentStatus(0);

    // Cleanup
    return () => {
      pollingRef.current = false;
    };
  }, [sessionId, token, navigate, refreshUser]);

  const handleGoToQuizzes = () => {
    navigate("/quizzes", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
      <motion.div 
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
          {verifying ? (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-teal-500 animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Verifying Payment...</h1>
              <p className="text-slate-600">Please wait while we confirm your payment.</p>
              {attempts > 0 && (
                <p className="text-sm text-slate-400 mt-2">Checking... (attempt {attempts}/{maxAttempts})</p>
              )}
            </>
          ) : verified ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3" data-testid="success-title">Welcome to Premium!</h1>
              <p className="text-slate-600 mb-4">
                Your subscription is now active. You have full access to all quizzes and mock exams.
              </p>
              <p className="text-sm text-teal-600 mb-6 animate-pulse">
                Redirecting you to quizzes...
              </p>
              <Button 
                onClick={handleGoToQuizzes}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="start-practicing-btn"
              >
                Start Practicing Now
              </Button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Verification Issue</h1>
              <p className="text-slate-600 mb-6">
                {error || "We couldn't verify your subscription. Please try again or contact support."}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.reload()}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                >
                  Refresh & Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/quizzes")}
                  className="w-full"
                >
                  Go to Quizzes (if paid)
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/auth")}
                  className="w-full text-slate-500"
                >
                  Back to Subscribe
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
